/**
 * PoliceDetailScreen — T2 Police entity-detail page.
 * Route: /policen/$id
 * Ported from v0 components/brokly/policy-detail-sheet.tsx (FactRow block),
 * rendered as a full-page shell (NOT a Sheet) per T2 design.
 * V10-J2: live-wired via usePoliceDetail (GET /policies/:id + /versions for the
 * current premium) + useRechnungenForKunde; mock fallback via VITE_DATA_MODE.
 *
 * DMS M12 (dms/spec.md §4.5, §M12): the Übersicht tab became "Stammdaten",
 * restructured into the reference screenshots' two-column Police/Finanzen
 * fact cards (§4.5's "Stammdaten (auto-filled from Docpier — Policen-Nr.,
 * Carrier, Sparte, Vertragsbeginn/-ende, Renewal in N Tagen, Jahresprämie,
 * Stempelabgabe…)"), plus a "Dokumente öffnen" card block: "Police öffnen"
 * opens the policy's current source document in M11's REUSED inline viewer
 * (`/ablage/$id`, same tab — no second viewer). VS/Selbstbehalt/Courtage from
 * the screenshot have NO backing wire field yet (only `PolicyVersionDto`'s
 * `premium_net`/`premium_gross`/`stamp_duty`/`source_document_id` exist) —
 * they're deliberately omitted rather than invented; see the module's report
 * for this deviation.
 * [Source: docs/stories/t2-entity-detail.md#task-4]
 * [Source: docs/stories/v10-j2-web-wiring-rest.md AC5]
 * [Source: projects/broker-ag/dms/spec.md §4.5, §M12]
 */
import type { PolicyDto, PolicyRegistrationPreviewDto } from '@brokly/shared';
import { useState } from 'react';
import {
  ScrollText,
  ReceiptText,
  FileText,
  History,
  Upload,
  Trash2,
  Pencil,
  Building2,
  ExternalLink,
  CheckCircle2,
  Gauge,
} from 'lucide-react';
import { useParams, useNavigate, useSearch } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { PAYMENT_FREQUENCY_LABELS } from '@brokly/shared';
import { Button } from '../../components/ui/Button';
import { StatusDot } from '../../components/ui/StatusDot';
import { DataTable } from '../../components/data/DataTable';
import { EntityLink } from '../../components/ui/EntityLink';
import { DetailShell } from './components/DetailShell';
import type { ActionItem } from './components/ActionsMenu';
import { FaelligText } from './components/FaelligText';
import { Timeline } from './components/Timeline';
import { iconForSparte } from './components/SparteBadge';
import { POLICY_STATUS_META, getCarrierByName } from './mock';
import { usePoliceDetail, useRechnungenForKunde } from './hooks/useEntityDetail';
import { PolicyAvbSection } from './PolicyAvbSection';
import { PolicyEditDialog } from './components/PolicyEditDialog';
import { RisikoDeckungTab } from './components/police-risiko/RisikoDeckungTab';
import type { KundeRechnung } from './types';
import { chf } from '../../lib/format';
import { apiClient } from '../../lib/api-client';
import { qk } from '../../lib/query-keys';
import { useEffectivePermissions } from '../administration/useEffectivePermissions';
import { useEffectiveVisibility } from '../administration/useEffectiveVisibility';
import { useDocumentDetail } from '../documents/hooks/useDocumentDetail';
import { useDeleteDocumentAction } from '../documents/hooks/useDeleteDocumentAction';

function FactRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium text-foreground">{children}</span>
    </div>
  );
}

/**
 * Bereich 3 (section/card visibility): the Police-detail tab `value` → visibility
 * catalog TAB key. `stammdaten` is the shipped tab value for the catalog's
 * `policen.uebersicht` tab (naming predates the catalog).
 */
const POLICE_TAB_VISIBILITY_KEY: Record<string, string | undefined> = {
  stammdaten: 'policen.uebersicht',
  risiko: 'policen.risiko_deckung', // P2 Placement Slice 2
  rechnungen: 'policen.rechnungen',
  dokumente: 'policen.dokumente',
  aktivitaet: 'policen.aktivitaet',
};

export function PoliceDetailScreen() {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const perms = useEffectivePermissions();
  // Bereich 3: the tab-AND-card visibility layer. `isHidden(key)` REMOVES a tab or
  // card from the policy-detail UI for a role whose effective visibility denies it.
  const { isHidden } = useEffectiveVisibility();

  // Server state (V10-J2) — all hooks BEFORE any early return.
  const { data: detail, isLoading } = usePoliceDetail(id);
  const policy = detail?.policy;
  // DMS M12: the open policy_version — Stempelabgabe/Nettoprämie + the source
  // PDF for "Dokumente öffnen" (§4.5). `null` when the policy has no open
  // version — every consumer below renders that honestly ('—' / hidden card).
  const currentVersion = detail?.currentVersion ?? null;
  // Rechnungen for this policy's customer (live: GET /invoices?subject…).
  // E5: when the Rechnungen tab is denied, pass `undefined` subjectType so the hook's
  // `enabled: Boolean(subjectType)` gate skips the fetch in live mode — a denied tab must
  // never ship its data to the client (this feed is used ONLY by the Rechnungen tab).
  const rechnungenSubjectType = isHidden('policen.rechnungen')
    ? undefined
    : policy
      ? detail?.subjectType
      : undefined;
  const { data: alleRechnungen = [] } = useRechnungenForKunde(
    policy?.kundeId ?? '',
    rechnungenSubjectType,
  );

  // DMS Part2 P2-13 (§1.3): "Löschen" targets the CURRENT source document. Gated
  // `document.delete` (hides the action entirely, like every other permission-gated
  // control) + live document state (disables it with a tooltip while active — an
  // in-force policy's current PDF can't be deleted; only a superseded/inactive one).
  const canDeleteDoc = perms.can('document.delete');
  const currentDocumentId = currentVersion?.source_document_id ?? null;
  const { detail: currentDocDetail } = useDocumentDetail(
    canDeleteDoc && currentDocumentId ? currentDocumentId : '',
  );
  const { deleteDocument } = useDeleteDocumentAction();
  // DMS Part2 P2-4 (§4.6): the manual Stammdaten edit, `policy.write`-gated.
  const canWritePolicy = perms.can('policy.write');
  // P2 Placement Slice 2: the Risiko-&-Deckung edit, `policy_risk.write`-gated.
  const canWriteRisk = perms.can('policy_risk.write');
  const [editOpen, setEditOpen] = useState(false);

  // P2 Placement Slice 2: the initial tab can come from ?tab= («Details je Police» deep-link).
  // Route-agnostic read (strict:false) — the standalone test router mounts its own /policen/$id.
  const search = useSearch({ strict: false }) as { tab?: string };
  const [activeTab, setActiveTab] = useState(search.tab ?? 'stammdaten');
  const [feedbackMsg, setFeedbackMsg] = useState<string | null>(null);

  function showFeedback(msg: string) {
    setFeedbackMsg(msg);
    setTimeout(() => setFeedbackMsg(null), 2000);
  }

  /**
   * Issue 24: the manual draft -> active promotion (`sistiert` is the web label for the
   * wire's `draft`). `policy.write` gated here for the UI; the api re-enforces it and
   * rejects a non-draft with 409.
   */
  async function handleActivate() {
    if (!policy) return;
    // P5-S7: activating may report the Brokermandat to the insurer (automatische Übertragung): say so first.
    // A failed preview asks anyway (fails closed).
    let preview: PolicyRegistrationPreviewDto | null = null;
    try {
      preview = await apiClient.policies.registrationPreview(id);
    } catch {
      preview = null;
    }
    const insurer = preview?.carrier_name ?? 'den Versicherer';
    const question =
      preview === null
        ? 'Mit dem Aktivieren wird das Brokermandat möglicherweise an den Versicherer gemeldet. Fortfahren?'
        : preview.meldung && preview.freigabe
          ? `Mit dem Aktivieren wird eine Meldung des Brokermandats an ${insurer} vorbereitet, die ein Broker mit Versandrecht freigibt. Fortfahren?`
          : preview.meldung
            ? `Mit dem Aktivieren wird das Brokermandat an ${insurer} gemeldet. Fortfahren?`
            : null;
    if (question !== null && !window.confirm(question)) return;
    let activated: PolicyDto;
    try {
      activated = await apiClient.policies.activate(id);
    } catch {
      window.alert('Aktivieren fehlgeschlagen.');
      return;
    }
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['policies'] }),
      qc.invalidateQueries({ queryKey: ['customers'] }),
      // the activation may link the policy to a registration, plan a Meldung or open a task
      qc.invalidateQueries({ queryKey: ['registrations'] }),
      qc.invalidateQueries({ queryKey: ['tasks'] }),
    ]);
    if (activated.registration_hook_failed) {
      showFeedback('Police aktiviert. Die Meldung an den Versicherer ist fehlgeschlagen: Aufgabe «Vererbung prüfen».');
    } else if (preview?.meldung && preview.freigabe) {
      showFeedback(`Police aktiviert, Meldung an ${insurer} wartet auf Freigabe`);
    } else if (preview?.meldung) {
      showFeedback(`Police aktiviert, Brokermandat wird an ${insurer} übertragen`);
    } else {
      showFeedback('Police aktiviert');
    }
  }

  async function handleDeleteCurrentDocument() {
    if (!currentDocumentId || !policy) return;
    if (await deleteDocument(currentDocumentId, `Police ${policy.nr}`)) {
      showFeedback('Dokument gelöscht');
    }
  }

  if (!policy) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-12 text-center">
        <p className="text-sm text-muted-foreground">
          {isLoading ? 'Lade Police …' : 'Police nicht gefunden.'}
        </p>
        {!isLoading && (
          <Button variant="outline" size="sm" onClick={() => void navigate({ to: '/policen' })}>
            Zurück zu Policen
          </Button>
        )}
      </div>
    );
  }

  const s = POLICY_STATUS_META[policy.status];
  const SparteIcon = iconForSparte(policy.sparte) ?? ScrollText;

  const rechnungColumns: ColumnDef<KundeRechnung>[] = [
    {
      accessorKey: 'nr',
      header: 'Rechnungs-Nr.',
      cell: ({ row }) => <span className="mono-num text-xs">{row.original.nr}</span>,
    },
    {
      accessorKey: 'datum',
      header: 'Datum',
      cell: ({ row }) => <span className="text-muted-foreground">{row.original.datum}</span>,
    },
    {
      accessorKey: 'betrag',
      header: 'Betrag',
      cell: ({ row }) => (
        <div className="text-right mono-num">{chf(row.original.betrag)}</div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <StatusDot tone={row.original.status.tone} label={row.original.status.label} />
      ),
    },
  ];

  // Resolve carrier id for navigation (V10-J2: live rows carry the carrier_uid)
  function navigateToCarrier(versichererName: string) {
    const carrierId = policy?.versichererId ?? getCarrierByName(versichererName)?.id;
    if (carrierId) {
      void navigate({ to: `/versicherer/${carrierId}` as never });
    }
  }

  const allTabs = [
    { value: 'stammdaten', label: 'Stammdaten', icon: ScrollText },
    { value: 'risiko', label: 'Risiko & Deckung', icon: Gauge },
    { value: 'rechnungen', label: 'Rechnungen', icon: ReceiptText, count: alleRechnungen.length },
    { value: 'dokumente', label: 'Dokumente', icon: FileText },
    { value: 'aktivitaet', label: 'Aktivität', icon: History },
  ];
  // Bereich 3 (tab-level): drop tabs whose catalog key is denied for this role.
  const tabs = allTabs.filter((t) => {
    const key = POLICE_TAB_VISIBILITY_KEY[t.value];
    return key === undefined || !isHidden(key);
  });
  // Fall back to the first still-visible tab when the current one was hidden.
  const shownTab = tabs.some((t) => t.value === activeTab) ? activeTab : tabs[0]?.value ?? activeTab;

  // E4 (consistency with the Kunde header): the Kopfbereich Jahresprämie is the flagship
  // sensitive premium (`policen.praemie`, also server-gated) and duplicates the Finanzen
  // card — gate the header number so denying either key removes it from the header too,
  // not just from the card.
  const headerPraemieHidden =
    isHidden('policen.praemie') || isHidden('policen.uebersicht.finanzen');

  // DMS Part2 P2-4 (§4.6) + P2-13 (§1.3): both header actions are permission-gated —
  // hidden entirely when the caller lacks the write/delete permission (defense in
  // depth; the api still enforces both regardless). "Löschen" additionally disables
  // (with a tooltip) when the current document isn't inactive yet.
  const headerActions: ActionItem[] = [
    { label: 'Dokument hochladen', icon: Upload, onClick: () => showFeedback('Upload …') },
    ...(canWritePolicy
      ? [{ label: 'Bearbeiten', icon: Pencil, onClick: () => setEditOpen(true) } satisfies ActionItem]
      : []),
    // Only offered on a sistierte (wire `draft`) Police, and only with policy.write.
    ...(canWritePolicy && policy.status === 'sistiert'
      ? [
          {
            label: 'Aktivieren',
            icon: CheckCircle2,
            onClick: () => void handleActivate(),
          } satisfies ActionItem,
        ]
      : []),
    ...(canDeleteDoc
      ? [
          {
            label: 'Löschen',
            icon: Trash2,
            destructive: true,
            separator: true,
            disabled: !currentDocumentId || currentDocDetail?.state !== 'inactive',
            disabledReason: 'Zuerst auf inaktiv setzen.',
            onClick: () => void handleDeleteCurrentDocument(),
          } satisfies ActionItem,
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-0 p-4">
      {feedbackMsg && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-lg border border-sem-ok/40 bg-sem-ok-bg px-4 py-2.5 text-sm text-sem-ok shadow-md"
        >
          {feedbackMsg}
        </div>
      )}

      <DetailShell
        icon={SparteIcon}
        iconClassName="bg-foreground text-background"
        title={<span className="mono-num">{policy.nr}</span>}
        status={<StatusDot tone={s.tone} label={s.label} />}
        meta={
          <span className="flex items-center gap-1.5 flex-wrap">
            <EntityLink
              onActivate={() =>
                void navigate({ to: '/customers/$id', params: { id: policy.kundeId } })
              }
            >
              {policy.kunde}
            </EntityLink>
            <span className="text-muted-foreground">·</span>
            <Building2 className="size-3.5 text-muted-foreground" aria-hidden />
            <EntityLink onActivate={() => navigateToCarrier(policy.versicherer)}>
              {policy.versicherer}
            </EntityLink>
          </span>
        }
        keyNumberLabel={headerPraemieHidden ? undefined : 'Jahresprämie'}
        keyNumber={
          headerPraemieHidden ? undefined : policy.praemie === null ? '-' : chf(policy.praemie)
        }
        actions={headerActions}
        backTo={{ label: 'Policen', onClick: () => void navigate({ to: '/policen' }) }}
        tabs={tabs}
        primaryCount={5}
        activeTab={shownTab}
        onTabChange={setActiveTab}
      >
        {/* Stammdaten — auto-filled from Docpier extraction (dms/spec.md §4.5).
            Two-column Police/Finanzen fact cards (screenshots 04-06 structure;
            Brokly design system, not the prototype visuals) + "Dokumente
            öffnen" trigger into the M11 inline viewer. */}
        {shownTab === 'stammdaten' && (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {/* Bereich 3: the Police-Stammdaten fact card, removed when hidden. */}
              {!isHidden('policen.uebersicht.stammdaten') && (
              <div className="rounded-lg border bg-card px-3 divide-y">
                <FactRow label="Policen-Nr.">
                  <span className="mono-num">{policy.nr}</span>
                </FactRow>
                {/* DMS Part2 P2-4 (WS4 §4.2): auto-generated/broker-set policy label. */}
                <FactRow label="Bezeichnung">{policy.bezeichnung ?? '-'}</FactRow>
                <FactRow label="Carrier">
                  <EntityLink onActivate={() => navigateToCarrier(policy.versicherer)}>
                    {policy.versicherer}
                  </EntityLink>
                </FactRow>
                <FactRow label="Sparte">{policy.sparte}</FactRow>
                <FactRow label="Kunde">
                  <EntityLink
                    onActivate={() =>
                      void navigate({ to: '/customers/$id', params: { id: policy.kundeId } })
                    }
                  >
                    {policy.kunde}
                  </EntityLink>
                </FactRow>
                <FactRow label="Status">
                  <StatusDot tone={s.tone} label={s.label} />
                </FactRow>
                <FactRow label="Vertragsbeginn">{policy.beginn}</FactRow>
                <FactRow label="Vertragsende">{policy.ende ?? '-'}</FactRow>
                <FactRow label="Nächstes Renewal">
                  <FaelligText restTage={policy.renewalTage} showIcon={false} />
                </FactRow>
              </div>
              )}

              {/* Bereich 3: the Finanzen fact card, removed when hidden. */}
              {!isHidden('policen.uebersicht.finanzen') && (
              <div className="rounded-lg border bg-card px-3 divide-y">
                <FactRow label="Jahresprämie">
                  {policy.praemie === null ? (
                    '-'
                  ) : (
                    <span className="mono-num">{chf(policy.praemie)}</span>
                  )}
                </FactRow>
                {/* DMS Part2 P2-4 (WS4 §4.1): already auto-filled from Docpier — surface only. */}
                <FactRow label="Zahlungsart">
                  {policy.paymentFrequency ? PAYMENT_FREQUENCY_LABELS[policy.paymentFrequency] : '-'}
                </FactRow>
                <FactRow label="Nettoprämie">
                  {currentVersion?.premium_net ? (
                    <span className="mono-num">{chf(Number(currentVersion.premium_net))}</span>
                  ) : (
                    '-'
                  )}
                </FactRow>
                <FactRow label="Eidg. Stempelabgabe">
                  {currentVersion?.stamp_duty ? (
                    <span className="mono-num">{chf(Number(currentVersion.stamp_duty))}</span>
                  ) : (
                    '-'
                  )}
                </FactRow>
              </div>
              )}
            </div>

            {/* "Dokumente öffnen" (dms/spec.md §4.5, §M12) — opens M11's REUSED
                inline viewer at /ablage/$id, same tab, for the policy's
                current source document (policy_version.source_document_id).
                Bereich 3: removed when `policen.uebersicht.dokument` is hidden. */}
            {!isHidden('policen.uebersicht.dokument') && (
            <div className="rounded-lg border bg-card p-4">
              <h3 className="text-sm font-semibold text-foreground">Dokumente öffnen</h3>
              {currentVersion?.source_document_id ? (
                <button
                  type="button"
                  onClick={() =>
                    void navigate({
                      to: '/ablage/$id',
                      params: { id: currentVersion.source_document_id! },
                    })
                  }
                  className="mt-3 flex w-full items-center gap-3 rounded-lg border border-border p-3 text-left transition-colors hover:border-primary/50"
                >
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                    <FileText className="size-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-foreground">
                      Police öffnen
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {policy.sparte} · Police {policy.nr}
                    </span>
                  </span>
                  <ExternalLink className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                </button>
              ) : (
                <p className="mt-3 text-sm text-muted-foreground">
                  Kein Quelldokument für diese Police hinterlegt.
                </p>
              )}
            </div>
            )}
          </div>
        )}

        {/* Risiko & Deckung (P2 Placement Slice 2) — branchenspezifische Ist-Attribute,
            Soll-Deckung & Gap, versicherte Objekte. Sections are self-gated by their Kacheln. */}
        {shownTab === 'risiko' && (
          <RisikoDeckungTab
            policyId={id}
            canWrite={canWriteRisk}
            isHidden={isHidden}
            onSetSparte={() => setEditOpen(true)}
          />
        )}

        {/* Rechnungen — Bereich 3: removed when `policen.rechnungen.liste` is hidden. */}
        {shownTab === 'rechnungen' && !isHidden('policen.rechnungen.liste') && (
          <DataTable columns={rechnungColumns} data={alleRechnungen} />
        )}

        {/* Dokumente — Versicherungsbedingungen (AVB) + auto-match (Wave global-avb).
            Bereich 3: the AVB card is removed when `policen.dokumente.avb` is hidden. */}
        {shownTab === 'dokumente' && (
          <div className="flex flex-col gap-4">
            {!isHidden('policen.dokumente.avb') && (
              <PolicyAvbSection
                policyId={id}
                onOpenDocument={(documentId) =>
                  void navigate({ to: '/ablage/$id', params: { id: documentId } })
                }
              />
            )}
            <div className="rounded-lg border bg-card p-6 text-center text-sm text-muted-foreground">
              Weitere Dokumente: in Arbeit.
            </div>
          </div>
        )}

        {/* Aktivität stub — Bereich 3: removed when `policen.aktivitaet.verlauf` is hidden. */}
        {shownTab === 'aktivitaet' && !isHidden('policen.aktivitaet.verlauf') && (
          <Timeline
            events={[]}
            empty="Keine Aktivität erfasst."
          />
        )}
      </DetailShell>

      {/* DMS Part2 P2-4 (§4.6/§4.8): the manual Stammdaten edit dialog. */}
      {editOpen && (
        <PolicyEditDialog
          policyId={id}
          initial={{
            bezeichnung: policy.bezeichnung ?? null,
            praemienbefreiung: policy.praemienbefreiung ?? false,
            praemienbefreiungSeit: policy.praemienbefreiungSeit ?? null,
            vermittlerPersonId: policy.vermittlerPersonId ?? null,
            bemerkungen: policy.bemerkungen ?? null,
            igb2bCode: policy.igb2bCode ?? null,
            carrierUid: policy.versichererId ?? null,
          }}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            void qc.invalidateQueries({ queryKey: qk.policy(id) });
            showFeedback('Police gespeichert');
          }}
        />
      )}
    </div>
  );
}
